import React from "react";
import { Link } from "react-router-dom";
import {
  CardDefinition,
  CardInstance,
  CardRarity,
  LobbySnapshot,
  OwnedCardTile,
  Playability,
  useGetLobbyTodayQuery,
  useGetLockedSummaryQuery,
  useGetMyCardsQuery,
  useGetTodayDropQuery,
} from "../api/respectCardsApi";
import { useAppSelector } from "../../../store/hooks";
import RespectCard from "../components/RespectCard";
import PlayCardModal from "../components/PlayCardModal";
import CardCatalogModal from "../components/CardCatalogModal";
import CardRevealOverlay from "../components/CardRevealOverlay";
import CardTile from "../components/CardTile";
import MyEffectsList from "../components/MyEffectsList";
import DevCardSimulatorPanel from "../components/DevCardSimulatorPanel";
import { checkRole } from "../../../components/recipe_page/recipe_page";
import { getApiErrorMessage, humanizeUntil } from "../lib/utils";
import {
  comparePlayableFirst,
  isGameCard,
  isHistory,
  isInHand,
} from "../lib/cardState";
import { useNowTicker } from "../hooks/useNowTicker";
import "../styles/cards.css";
import "../styles/cardsDeck.css";
import "../styles/dropLobby.css";

const IS_DEV = process.env.NODE_ENV !== "production";

const TIER_ORDER: CardRarity[] = ["legendary", "epic", "rare", "common"];

const CATEGORY_ICON: Record<string, string> = {
  aotd: "🎨",
  comments: "💬",
  economy: "💰",
  respect: "🏆",
  social: "🤝",
  notification: "🔔",
  "agent-influence": "🎲",
};

function formatOpensAtLocal(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface TodayDropHeroProps {
  state: "loading" | "error" | "no-drop" | "locked" | "open" | "claimed" | "logged-out";
  tiers: CardRarity[];
  opensAt?: string;
  errorMessage?: string;
  pulledCard?: CardDefinition | null;
  /** From the shared lobby snapshot — drives the drained / late-open copy. */
  remainingSupply?: number | null;
  lateEntryOpen?: boolean;
  lobbyDone?: boolean;
}

function TodayDropHero({
  state,
  tiers,
  opensAt,
  errorMessage,
  pulledCard,
  remainingSupply,
  lateEntryOpen,
  lobbyDone,
}: TodayDropHeroProps) {
  const heroMod =
    state === "open" ? "open" : state === "claimed" ? "claimed" : "locked";

  const stateLabel =
    state === "open"
      ? "Open"
      : state === "claimed"
      ? "Claimed"
      : state === "locked"
      ? "Locked"
      : "";

  const countdown =
    state === "locked" && opensAt ? humanizeUntil(opensAt) : null;

  const drained = remainingSupply === 0;
  const lateOpen = Boolean(lobbyDone && lateEntryOpen && !drained);

  // Open-state copy, corrected: the drop IS open here, so never say "you'll get
  // a notification when it opens". Reflect the drained / late-open pool state.
  const openCopy = drained ? (
    "All of today's cards are claimed — back tomorrow."
  ) : lateOpen ? (
    <>
      {remainingSupply != null ? `${remainingSupply} left · ` : ""}late pull open — jump
      into the drop while cards last.
    </>
  ) : (
    "Today's drop is live — a 45-second race for the best odds. Miss it and you can still pull late, at reduced odds, while cards last."
  );

  return (
    <section
      className={`cards-deck-hero cards-deck-hero--${heroMod}`}
      aria-label="Today's drop"
    >
      <div className="cards-deck-hero__eyebrow">Today&apos;s drop</div>
      {stateLabel ? (
        <span
          className={`cards-deck-hero__state-pill cards-deck-hero__state-pill--${heroMod}`}
        >
          {stateLabel}
        </span>
      ) : null}

      {state === "loading" ? (
        <p className="cards-deck-hero__msg">Loading…</p>
      ) : state === "error" ? (
        <p className="cards-deck-hero__msg">{errorMessage}</p>
      ) : state === "no-drop" ? (
        <p className="cards-deck-hero__msg">No drop available right now.</p>
      ) : (
        <>
          <div
            className={`cards-deck-hero__top-card${
              state === "claimed" && pulledCard
                ? " cards-deck-hero__top-card--revealed"
                : ""
            }`}
            aria-label={
              state === "claimed" && pulledCard
                ? `Today's pulled card: ${pulledCard.name}`
                : "Today's card slot"
            }
          >
            {state === "claimed" && pulledCard ? (
              <RespectCard card={pulledCard} size="md" expandable={false} />
            ) : (
              <div className="cards-deck-hero__top-card-face">?</div>
            )}
          </div>

          {tiers.length > 0 ? (
            <div
              className="cards-deck-hero__tier-dots"
              aria-label="Tiers in today's pool"
            >
              {tiers.map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className={`cards-deck-hero__tier-dot cards-deck-hero__tier-dot--${t}`}
                  title={t}
                />
              ))}
            </div>
          ) : null}

          {state === "locked" && opensAt ? (
            <div className="cards-deck-hero__timer">
              Opens at <strong>{formatOpensAtLocal(opensAt)}</strong>
              {countdown && countdown !== "expired" ? (
                <> · in <strong>{countdown}</strong></>
              ) : null}
            </div>
          ) : null}

          {state === "open" ? (
            <p className="cards-deck-hero__msg">{openCopy}</p>
          ) : state === "claimed" ? (
            <p className="cards-deck-hero__msg">
              Claimed today — see you tomorrow 🌅
            </p>
          ) : state === "logged-out" ? (
            <p className="cards-deck-hero__msg">
              <Link to="/login">Log in</Link> to claim today&apos;s card.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * cards-drop-lobby — when a live shared drop is gathering or revealing, surface
 * a CTA into the lobby. Reads the shared lobby snapshot lifted into CardsPage
 * (one 20s poll feeds both this and the hero).
 */
function LobbyBanner({ lobby }: { lobby: LobbySnapshot | null }) {
  if (!lobby) return null;
  const youPulled = lobby.participants.some(
    (p) => p.isYou && p.pullStatus === "done",
  );
  if (lobby.status === "done" && (!lobby.lateEntryOpen || youPulled)) return null;
  const remaining = lobby.remainingSupply ?? null;
  const label =
    lobby.status === "gathering"
      ? `🃏 A card drop is gathering — ${lobby.participantCount} in the lobby!`
      : lobby.status === "revealing"
      ? "🃏 A card drop is live — pulls happening now!"
      : remaining != null
      ? `🃏 ${remaining} card${remaining === 1 ? "" : "s"} still unclaimed — late pull open`
      : "🃏 Late pull open — while cards last";
  // What the viewer will be charged at the door (losers' bracket). Server-quoted;
  // absent for logged-out reads, in which case we say nothing about price.
  const entry = lobby.yourEntry ?? null;
  const entryNote = !entry
    ? null
    : entry.role === "loser"
    ? " Your pull is free."
    : entry.free
    ? null
    : entry.lateEntryOnly && lobby.status === "gathering"
    ? ` You didn't race, so late entry is your lane: ${entry.fee} respect.`
    : ` Entry costs ${entry.fee} respect.`;
  return (
    <div className="drop-lobby-banner">
      <span className="drop-lobby-banner__text">
        {label}
        {entryNote}
      </span>
      <Link to="/cards/drop" className="drop-lobby-banner__cta">
        {lobby.status === "done" ? "Try a late pull →" : "Join the drop →"}
      </Link>
    </div>
  );
}

export default function CardsPage() {
  const auth = useAppSelector((state) => state.auth);
  const isAuthenticated = auth.bootstrapStatus === "ready" && Boolean(auth.token);
  const isAdmin = isAuthenticated && checkRole(auth.role ?? "", "admin");
  const showDevSimulator = IS_DEV && isAdmin;

  useNowTicker(30_000);

  const {
    data: dropData,
    isLoading: dropLoading,
    isError: dropError,
    error: dropErr,
    refetch: refetchDrop,
  } = useGetTodayDropQuery();

  // Amendment 7 — ONE shared lobby subscription (20s poll) feeding both the
  // hero's drained/late-open states and the LobbyBanner CTA.
  const { data: lobbyData } = useGetLobbyTodayQuery(undefined, {
    pollingInterval: 20_000,
  });
  const lobby: LobbySnapshot | null =
    lobbyData && typeof lobbyData === "object" ? lobbyData.lobby : null;

  const {
    data: myCardsData,
    isLoading: myCardsLoading,
    refetch: refetchMyCards,
  } = useGetMyCardsQuery(undefined, { skip: !isAuthenticated });

  const { data: lockedData } = useGetLockedSummaryQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [playTarget, setPlayTarget] = React.useState<CardInstance | null>(null);
  const [playTargetPlayability, setPlayTargetPlayability] =
    React.useState<Playability | null>(null);
  const [collectionOpen, setCollectionOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [revealed, setRevealed] = React.useState<{
    card: CardDefinition;
    headline?: string;
  } | null>(null);

  React.useEffect(() => {
    document.title = "NΛSOS — Cards";
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => refetchMyCards();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthenticated, refetchMyCards]);

  // `card_expiry_played` deep-links /cards#effects — scroll the Running effects
  // section into view once the hand data has loaded and the anchor exists.
  React.useEffect(() => {
    if (window.location.hash !== "#effects") return;
    if (!isAuthenticated || myCardsLoading) return;
    const el = document.getElementById("effects");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isAuthenticated, myCardsLoading]);

  const ownedTiles = React.useMemo(
    () => myCardsData?.tiles ?? [],
    [myCardsData?.tiles],
  );

  // In-hand tiles, playable-first then soonest-expiry (server playability).
  const handTiles = React.useMemo(
    () => ownedTiles.filter(isInHand).slice().sort(comparePlayableFirst),
    [ownedTiles],
  );

  // History = nothing in hand (played / expired / consumed) + game cards.
  const historyTiles = React.useMemo(
    () => ownedTiles.filter(isHistory),
    [ownedTiles],
  );

  // Server playability count — "N unique · M playable".
  const playableCount = React.useMemo(
    () =>
      handTiles.reduce((acc, t) => {
        const s = t.playability?.state;
        return acc + (s === "ready" || s === "needs_target" ? 1 : 0);
      }, 0),
    [handTiles],
  );

  // Per-card in_hand instances, so a multi-copy tile can enumerate expiries.
  const handInstancesByCardId = React.useMemo(() => {
    const m = new Map<string, CardInstance[]>();
    for (const c of myCardsData?.cards ?? []) {
      if (c.status !== "in_hand") continue;
      const arr = m.get(c.cardId) ?? [];
      arr.push(c);
      m.set(c.cardId, arr);
    }
    return m;
  }, [myCardsData?.cards]);

  const closeReveal = React.useCallback(() => {
    setRevealed(null);
    refetchMyCards();
    refetchDrop();
  }, [refetchDrop, refetchMyCards]);

  // keep the freshest cards[] available to the stable openPlay callback
  const myCardsDataRef = React.useRef(myCardsData);
  React.useEffect(() => {
    myCardsDataRef.current = myCardsData;
  }, [myCardsData]);

  // A tile's Play button submits the tile's `playableInstanceId` — never the
  // newest-of-any-status instance. Build the modal target from that copy.
  const openPlay = React.useCallback((tile: OwnedCardTile) => {
    if (!tile.playableInstanceId || !tile.definition) return;
    const instances = myCardsDataRef.current?.cards ?? [];
    const match = instances.find((c) => c._id === tile.playableInstanceId);
    const target: CardInstance =
      match ?? {
        _id: tile.playableInstanceId,
        userId: "",
        cardId: tile.cardId,
        source: "daily_drop",
        status: "in_hand",
        acquiredAt: tile.acquiredAt,
        playedAt: null,
        effectExpiresAt: null,
        unplayedExpiresAt: tile.soonestUnplayedExpiresAt,
        definition: tile.definition,
      };
    setPlayTarget(target);
    setPlayTargetPlayability(tile.playability ?? null);
  }, []);

  // Derive hero state
  const dropIsOpen = Boolean(dropData?.isOpen);
  const userClaimed = Boolean(dropData?.userClaimed);
  const hasDropCards = (dropData?.tiers.length ?? 0) > 0;

  let heroState: TodayDropHeroProps["state"];
  if (dropLoading) heroState = "loading";
  else if (dropError || !dropData) heroState = "error";
  else if (!hasDropCards) heroState = "no-drop";
  else if (!isAuthenticated) heroState = "logged-out";
  else if (userClaimed) heroState = "claimed";
  else if (dropIsOpen) heroState = "open";
  else heroState = "locked";

  const heroErrorMessage =
    heroState === "error"
      ? getApiErrorMessage(dropErr, "Could not load today's drop.")
      : undefined;

  const pulledCardDef = React.useMemo<CardDefinition | null>(() => {
    const outcome = dropData?.userOutcome;
    if (!outcome || outcome.outcome !== "card" || !outcome.cardId) return null;
    const tile = ownedTiles.find((t) => t.cardId === outcome.cardId);
    return tile?.definition ?? null;
  }, [dropData?.userOutcome, ownedTiles]);

  const uniqueOwnedCount = ownedTiles.filter((t) => !isGameCard(t)).length;
  const totalCatalogSize =
    uniqueOwnedCount +
    Object.values(lockedData?.tiers ?? {}).reduce(
      (acc, t) => acc + (t?.count ?? 0),
      0,
    );

  const lateEntryOpen = Boolean(lobby?.lateEntryOpen);
  const remainingSupply = lobby?.remainingSupply ?? null;
  const nothingEverOwned = ownedTiles.length === 0;

  const handEmptyLine = (() => {
    if (lateEntryOpen && !nothingEverOwned) {
      return (
        <>
          Nothing in hand —{" "}
          <Link className="cards-deck-empty__cta" to="/cards/drop">
            late pull open{remainingSupply != null ? `, ${remainingSupply} left` : ""}
          </Link>
          .
        </>
      );
    }
    if (!nothingEverOwned && dropData?.opensAt) {
      return <>Nothing in hand — next drop at {formatOpensAtLocal(dropData.opensAt)}.</>;
    }
    return (
      <>Your hand is empty. Pull today&apos;s drop to start collecting.</>
    );
  })();

  return (
    <div className="cards-deck-wrap">
      <div className="cards-deck-page">
        <header className="cards-deck-header">
          <Link to="/art-of-the-day" className="cards-deck-header__back">
            ← AOTD
          </Link>
          <h1>Cards</h1>
          {isAuthenticated ? (
            <button
              type="button"
              className="cards-deck-header__collection"
              onClick={() => setCollectionOpen(true)}
            >
              📖 Collection
            </button>
          ) : (
            <span style={{ width: 100 }} />
          )}
        </header>

        <LobbyBanner lobby={lobby} />

        {showDevSimulator ? (
          <DevCardSimulatorPanel
            onReveal={(card, headline) => setRevealed({ card, headline })}
          />
        ) : null}

        <TodayDropHero
          state={heroState}
          tiers={dropData?.tiers ?? []}
          opensAt={dropData?.opensAt}
          errorMessage={heroErrorMessage}
          pulledCard={pulledCardDef}
          remainingSupply={remainingSupply}
          lateEntryOpen={lateEntryOpen}
          lobbyDone={lobby?.status === "done"}
        />

        {isAuthenticated ? (
          <section className="cards-deck-section" aria-label="In hand">
            <h2 className="cards-deck-section__title">
              In hand
              {handTiles.length > 0 ? (
                <span className="cards-deck-section__title-count">
                  {handTiles.length} unique · {playableCount} playable
                </span>
              ) : null}
            </h2>

            {myCardsLoading ? (
              <p className="cards-deck-empty">Loading your hand…</p>
            ) : handTiles.length === 0 ? (
              <p className="cards-deck-empty">{handEmptyLine}</p>
            ) : (
              <div className="cards-deck-grid">
                {handTiles.map((tile) => (
                  <CardTile
                    key={tile.cardId}
                    tile={tile}
                    handInstances={handInstancesByCardId.get(tile.cardId)}
                    onPlay={openPlay}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        {isAuthenticated ? (
          <section
            id="effects"
            className="cards-deck-section"
            aria-label="Running effects"
          >
            <h2 className="cards-deck-section__title">Running effects</h2>
            <MyEffectsList skip={!isAuthenticated} />
          </section>
        ) : null}

        {isAuthenticated && historyTiles.length > 0 ? (
          <section className="cards-deck-section" aria-label="History">
            <h2 className="cards-deck-section__title">
              History
              <button
                type="button"
                className="cards-deck-section__toggle"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-expanded={historyOpen}
              >
                {historyOpen ? "Hide" : `Show ${historyTiles.length}`}
              </button>
            </h2>

            {historyOpen ? (
              <div className="cards-deck-grid">
                {historyTiles.map((tile) => (
                  <CardTile key={tile.cardId} tile={tile} interactive={false} />
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {isAuthenticated && lockedData ? (
          <section className="cards-deck-section" aria-label="Yet to discover">
            <h2 className="cards-deck-section__title">
              Yet to discover
              {totalCatalogSize > 0 ? (
                <span className="cards-deck-section__title-count">
                  {uniqueOwnedCount}/{totalCatalogSize}
                </span>
              ) : null}
            </h2>

            <div className="cards-deck-locked">
              {TIER_ORDER.flatMap((tier) => {
                const summary = lockedData.tiers?.[tier];
                if (!summary || summary.count === 0) return [];
                return Array.from({ length: summary.count }).map((_, i) => (
                  <div
                    key={`${tier}-${i}`}
                    className={`cards-deck-locked__cell cards-deck-locked__cell--${tier}`}
                    aria-label={`Locked ${tier} card`}
                  >
                    <span className="cards-deck-locked__qm">?</span>
                    <span className="cards-deck-locked__tier">{tier}</span>
                    {i === 0 && summary.categories?.length ? (
                      <span className="cards-deck-locked__cats">
                        {summary.categories.slice(0, 3).map((c) => (
                          <span key={c} title={c}>
                            {CATEGORY_ICON[c] || "🎴"}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </div>
                ));
              })}
            </div>
          </section>
        ) : null}
      </div>

      <PlayCardModal
        card={playTarget}
        playability={playTargetPlayability}
        open={Boolean(playTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setPlayTarget(null);
            setPlayTargetPlayability(null);
          }
        }}
        onPlayed={() => {
          setPlayTarget(null);
          setPlayTargetPlayability(null);
          refetchMyCards();
        }}
      />

      <CardCatalogModal
        open={collectionOpen}
        onOpenChange={setCollectionOpen}
      />

      <CardRevealOverlay
        open={Boolean(revealed)}
        card={revealed?.card ?? null}
        headline={revealed?.headline}
        onClose={closeReveal}
      />
    </div>
  );
}
